import { FONT_FALLBACKS } from "@/elements/emoji/constants";

/**
 * Тип для кэша эмодзи
 * Ключ: эмодзи + размер шрифта, значение: base64 строка
 */
export type EmojiCache = Map<string, string>;

/**
 * Определяет, является ли кодовая точка частью эмодзи
 */
export function isEmojiCodePoint(codePoint: number): boolean {
    return (
        (codePoint >= 0x1f300 && codePoint <= 0x1f9ff) ||
        (codePoint >= 0x2600 && codePoint <= 0x26ff) ||
        (codePoint >= 0x2700 && codePoint <= 0x27bf) ||
        (codePoint >= 0x1f1e0 && codePoint <= 0x1f1ff) ||
        (codePoint >= 0x1f600 && codePoint <= 0x1f64f) ||
        (codePoint >= 0x1f680 && codePoint <= 0x1f6ff) ||
        (codePoint >= 0x1f900 && codePoint <= 0x1f9ff) ||
        (codePoint >= 0x1fa00 && codePoint <= 0x1fa6f) ||
        (codePoint >= 0x1fa70 && codePoint <= 0x1faff) ||
        (codePoint >= 0x2190 && codePoint <= 0x21ff) ||
        (codePoint >= 0x2300 && codePoint <= 0x23ff) ||
        (codePoint >= 0x2b50 && codePoint <= 0x2b55) ||
        (codePoint >= 0x3030 && codePoint <= 0x303f) ||
        (codePoint >= 0x3297 && codePoint <= 0x3299) ||
        (codePoint >= 0xfe00 && codePoint <= 0xfe0f)
    );
}

/**
 * Разбивает текст на части: обычный текст и эмодзи
 */
export function splitTextAndEmojis(
    text: string,
): Array<{ type: "text" | "emoji"; content: string }> {
    const result: Array<{ type: "text" | "emoji"; content: string }> = [];
    let currentText = "";

    for (let i = 0; i < text.length; i++) {
        const codePoint = text.codePointAt(i);

        // Проверяем, является ли символ частью эмодзи
        // Эмодзи могут состоять из нескольких символов (например, флаги, составные эмодзи)
        if (codePoint && isEmojiCodePoint(codePoint)) {
            // Если был накоплен текст, добавляем его
            if (currentText) {
                result.push({ type: "text", content: currentText });
                currentText = "";
            }

            // Добавляем эмодзи (может быть несколько символов)
            // Для составных эмодзи нужно собрать все связанные символы
            let emoji = String.fromCodePoint(codePoint);
            const charLength = codePoint > 0xffff ? 2 : 1; // Surrogate pairs занимают 2 символа
            let j = i + charLength;

            // Проверяем следующие символы для составных эмодзи
            while (j < text.length) {
                const nextCodePoint = text.codePointAt(j);
                if (!nextCodePoint) break;

                // Variation Selectors (FE00-FE0F), Combining Marks (200D) и другие эмодзи символы
                // (nextCodePoint === 0x200d) Zero Width Joiner
                if (nextCodePoint === 0x200d || isEmojiCodePoint(nextCodePoint)) {
                    emoji += String.fromCodePoint(nextCodePoint);
                    const nextCharLength = nextCodePoint > 0xffff ? 2 : 1;
                    j += nextCharLength;
                } else {
                    break;
                }
            }

            result.push({ type: "emoji", content: emoji });
            i = j - 1; // Пропускаем обработанные символы
        } else {
            // Обычный символ
            const char = text[i];
            currentText += char;
        }
    }

    // Добавляем оставшийся текст
    if (currentText) {
        result.push({ type: "text", content: currentText });
    }

    return result;
}

/**
 * Конвертирует эмодзи в base64 изображение для SVG.
 * Использует кэш для избежания повторной генерации одинаковых эмодзи
 */
export function emojiToBase64(emoji: string, fontSize: number, cache?: EmojiCache): string {
    const cacheKey = `${emoji}:${fontSize}`;

    // Проверяем кэш
    if (cache?.has(cacheKey)) {
        return cache.get(cacheKey)!;
    }

    // Создаем canvas для отрисовки эмодзи
    const canvas = document.createElement("canvas");
    const size = Math.ceil(fontSize * 1.2); // Размер canvas немного больше размера шрифта
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
        return "";
    }

    // Отрисовываем эмодзи с использованием fallback шрифтов
    // Используем тот же размер шрифта, что и в тексте
    const fontFamily = FONT_FALLBACKS.map((f) => `'${f}'`).join(", ");
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";

    const centerX = size / 2;
    const centerY = size * 0.8; // Позиционирование относительно базовой линии

    ctx.fillText(emoji, centerX, centerY);

    // Конвертируем в base64
    const base64 = canvas.toDataURL("image/png");

    // Сохраняем в кэш
    if (cache) {
        cache.set(cacheKey, base64);
    }

    return base64;
}

/**
 * Проверяет, содержит ли текст эмодзи
 */
export function hasEmoji(text: string): boolean {
    for (let i = 0; i < text.length; i++) {
        const codePoint = text.codePointAt(i);
        if (codePoint && isEmojiCodePoint(codePoint)) {
            return true;
        }
        // Пропускаем surrogate pairs
        if (codePoint && codePoint > 0xffff) {
            i++;
        }
    }
    return false;
}

/**
 * Заменяет текстовый элемент с эмодзи на группу с текстовыми элементами и изображениями эмодзи.
 * Возвращает группу для замены или null, если эмодзи нет в тексте.
 */
export function replaceTextWithEmojiImages(
    textElement: SVGTextElement,
    cache?: EmojiCache,
): SVGGElement | null {
    const textContent = textElement.textContent || "";
    if (!textContent) return null;

    // Проверяем, есть ли эмодзи в тексте
    if (!hasEmoji(textContent)) return null;

    // Получаем атрибуты текстового элемента (после установки шрифта)
    const x = parseFloat(textElement.getAttribute("x") || "0");
    const y = parseFloat(textElement.getAttribute("y") || "0");
    const fontSize = parseFloat(textElement.getAttribute("font-size") || "16");
    const fill = textElement.getAttribute("fill") || "black";
    const fontFamily = textElement.getAttribute("font-family") || "Arial";
    const fontWeight = textElement.getAttribute("font-weight") || "normal";
    const transform = textElement.getAttribute("transform") || "";

    // Разбиваем текст на части
    const parts = splitTextAndEmojis(textContent);

    // Создаем группу для замены текстового элемента
    const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
    if (transform) {
        group.setAttribute("transform", transform);
    }

    // Копируем все атрибуты родительского элемента
    Array.from(textElement.attributes).forEach((attr) => {
        if (
            attr.name !== "x" &&
            attr.name !== "y" &&
            attr.name !== "font-size" &&
            attr.name !== "fill" &&
            attr.name !== "font-family" &&
            attr.name !== "font-weight" &&
            attr.name !== "transform"
        ) {
            group.setAttribute(attr.name, attr.value);
        }
    });

    let currentX = x;

    // Создаем временный canvas для измерения ширины текста
    const measureCanvas = document.createElement("canvas");
    const measureCtx = measureCanvas.getContext("2d");
    if (measureCtx) {
        measureCtx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    }

    // Создаем элементы для каждой части
    parts.forEach((part) => {
        if (part.type === "text") {
            // Создаем текстовый элемент для обычного текста
            const textNode = document.createElementNS("http://www.w3.org/2000/svg", "text");
            textNode.setAttribute("x", currentX.toString());
            textNode.setAttribute("y", y.toString());
            textNode.setAttribute("font-size", fontSize.toString());
            textNode.setAttribute("fill", fill);
            textNode.setAttribute("font-family", fontFamily);
            textNode.setAttribute("font-weight", fontWeight);
            textNode.textContent = part.content;

            // Вычисляем ширину текста
            let textWidth = part.content.length * fontSize * 0.6; // Приблизительная ширина
            if (measureCtx) {
                const measured = measureCtx.measureText(part.content);
                textWidth = measured.width;
            }
            currentX += textWidth;

            group.appendChild(textNode);
        } else if (part.type === "emoji") {
            // Создаем изображение для эмодзи
            const image = document.createElementNS("http://www.w3.org/2000/svg", "image");
            const base64 = emojiToBase64(part.content, fontSize, cache);
            if (base64) {
                image.setAttribute("href", base64);
                // Позиционируем эмодзи относительно базовой линии текста.
                // В emojiToBase64 эмодзи рисуется с centerY = size * 0.8, где size = fontSize * 1.2.
                // Базовая линия эмодзи находится на centerY от верха canvas.
                // Используем полный размер canvas для width и height, чтобы эмодзи отрисовывалось в правильном размере.
                // Центрируем изображение относительно позиции текста, так как эмодзи в тексте занимает примерно fontSize по ширине.
                const canvasSize = Math.ceil(fontSize * 1.2);
                const centerY = canvasSize * 0.8;
                const offsetX = (canvasSize - fontSize) / 2;
                image.setAttribute("x", (currentX - offsetX).toString());
                image.setAttribute("y", (y - centerY).toString());
                image.setAttribute("width", canvasSize.toString());
                image.setAttribute("height", canvasSize.toString());
                currentX += fontSize;
                group.appendChild(image);
            }
        }
    });

    return group;
}
